import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import OnThisPageNav from '@/components/OnThisPageNav/OnThisPageNav';
import { useOverviewContext } from '@/context/OverviewContext';
import { studentPreviewRole, isTeacherPreview, tasklistDisplayGradeId } from '@/utils/toolSettingsUtils';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import {
  getVisibleContentSections,
  getVisibleKeepLearningEntries,
  getVisibleSubtasks,
  shouldShowTasklistSlice,
  type TasklistDisplayRuntime,
} from '@/utils/tasklistPageDisplay';
import {
  applyTasklistPageContent,
  buildTasklistLinkSources,
  isTasklistExternalHref,
  resolveTasklistButtonHref,
  type TasklistLinkSources,
} from '@/utils/resolveTasklistDynamicLinks';
import { useLinksContext } from '@/context/LinksContext';
import {
  loadTasklistPageProgress,
  saveTasklistPageProgress,
  type TasklistPageProgress,
} from '@/utils/tasklistSubtaskCompletionStorage';
import type {
  TasklistPageBlock,
  TasklistPageJson,
  TasklistPageSection,
  TasklistPageSubtask,
} from '@/types/tasklistPageJson';
import MarkdownText from '@/components/MarkdownText/MarkdownText';
import QuickLink from '@/components/QuickLink/QuickLink';
import { RedirectIcon } from '@/components/MenusIcons/MenusIcons';
import SubTaskList from '@/components/SubTaskList/SubTaskList';
import type { SubTaskListItem } from '@/components/SubTaskList/SubTaskList';
import TaskStatus from '@/components/TaskStatus/TaskStatus';
import CompletionCelebration from '@/components/CompletionCelebration/CompletionCelebration';
import { preloadCompletionCelebrationImages } from '@/components/CompletionCelebration/completionCelebrationAssets';
import ZoomableImage from '@/components/ZoomableImage/ZoomableImage';
import VidyardPlayer from '@/components/VidyardPlayer/VidyardPlayer';
import { parseVidyardUuid } from '@/utils/parseVidyardUuid';
import CompletionModal from '@/components/CompletionModal/CompletionModal';
import { useTasklist } from '@/context/TasklistContext';
import { useSchool } from '@/context/SchoolContext';
import { useTasklistDisplayRuntime } from '@/hooks/useTasklistDisplayRuntime/useTasklistDisplayRuntime';
import { useFilteredTasklistTasks } from '@/hooks/useFilteredTasklistTasks/useFilteredTasklistTasks';
import { parseTasklistPageFromWpPayload } from '@/utils/tasklistPageFromWp';
import { buildSchoolOverviewPageFromOnboarding } from '@/utils/tasklistPageSchoolOverviewFromWp';
import { subscribeLayoutBodyScroll, syncTocScrollSpyState } from '@/utils/tasklistTocScroll';
import {
  mergeSchoolOverviewPageFromWp,
} from '@/utils/tasklistPageSchoolOverviewMerge';
import {
  planTasklistSectionRenderUnits,
  sectionUsesQuickLinkStack,
} from '@/utils/tasklistJsonQuickLink';
import config from '@/config';
import {
  resolveHeroTaskPageRouteParam,
  resolveLearningCenterRouteParam,
  taskHeroPagePath,
  tasklistLearningCenterPagePath,
} from '@/utils/tasklistPath';
import { getCompletionModalNext } from '@/utils/learningCenterCompletionNav';
import { resolveLearningCenterBackendTaskId } from '@/utils/learningCenterTasksMerge';
import { resolveTasklistSubtaskImageSrc } from '@/utils/tasklistSubtaskImageSrc';
import { mergeSubtaskCompletionFromApi } from '@/utils/taskCompletionMerge';
import {
  mapOnboardingPageSubtasksFromApi,
  type ApiSubtaskAutocompleteRow,
} from '@/utils/onboardingSubtasksMergeFromApi';
import { tasksPayloadSubtasksForParent } from '@/utils/tasksPayloadSubtasksForParent';
import { readTasksSnapshotJson, writeTasksSnapshotFromJson } from '@/utils/tasksSnapshotSession';
import {
  cloneTasksPayloadWithLocalCompletionPatches,
  type LocalTasksSnapshotPatch,
} from '@/utils/tasksSnapshotPayloadPatch';
import {
  fetchTasksSnapshotAndPersist,
  putSubtaskComplete,
  putTaskComplete,
  type SubtaskCompleteActor,
} from '@/services/taskCompletionApiService';
import TasklistJsonPageHeader, {
  type TasklistJsonPageHeaderVariant,
} from '@/components/TasklistJsonPageHeader/TasklistJsonPageHeader';
import { WpErrorSection } from '@/components/ErrorFallbackSection/ErrorFallbackSection';

function withFallbackSubtaskTitlesForMerge(
  rows: ApiSubtaskAutocompleteRow[],
  visible: TasklistPageSubtask[]
): ApiSubtaskAutocompleteRow[] {
  if (rows.length !== visible.length) return rows;
  return rows.map((r, i) => ({
    ...r,
    taskTitle: r.taskTitle.trim().length ? r.taskTitle : String(visible[i]?.name ?? '').trim(),
  }));
}

/** Dev-only: explains why `PUT …/tasks/complete` was not called (see handleToggleSubtask). */
function logSubtaskToggleSkippedApiDev(args: {
  rowIndex: number;
  canCallTaskCompletionApi: boolean;
  subId: number;
  accessToken: string;
  identityId: string;
  relayTaskIdForSubtasksList: number | undefined;
  relayTaskIdForParentCompletion: number | undefined;
  subtaskCompleteActor: SubtaskCompleteActor | null;
}) {
  if (!import.meta.env.DEV) return;
  const {
    rowIndex,
    canCallTaskCompletionApi,
    subId,
    accessToken,
    identityId,
    relayTaskIdForSubtasksList,
    relayTaskIdForParentCompletion,
    subtaskCompleteActor,
  } = args;
  const reasons: string[] = [];
  if (!accessToken) reasons.push('no Okta accessToken (sign in)');
  if (!identityId) reasons.push('no selectedStudent.identityId');
  if (relayTaskIdForSubtasksList == null) reasons.push('no relay parent task id for GET /tasks subtask grouping');
  if (relayTaskIdForParentCompletion == null) reasons.push('no relay task id for PUT /tasks/complete (parent)');
  if (!subtaskCompleteActor) reasons.push('relay actor missing (LC userIdentityId / userAccountId)');
  if (canCallTaskCompletionApi && !subId) {
    reasons.push(
      `no CMS subTaskId for visible row ${rowIndex} — GET /tasks list must expose subtasks for parent task ${relayTaskIdForSubtasksList ?? '?'}, or names must match`
    );
  }
  console.info('[onboarding] Subtask toggle: no API call —', reasons.join(' · ') || 'see canCallTaskCompletionApi / subId');
}

const WHAT_NEXT_ID = 'tasklist-what-to-do-next';
const KEEP_LEARNING_ID = 'tasklist-keep-learning';

function slugify(text: string): string {
  return (
    text
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'section'
  );
}

function sectionAnchorId(section: Pick<TasklistPageSection, 'title' | 'id'>, index: number): string {
  const cmsId = section.id?.trim();
  if (cmsId) return cmsId;
  return `tasklist-section-${index}-${slugify(section.title)}`;
}

function subtaskAnchorId(name: string, index: number): string {
  return `tasklist-subtask-${index}-${slugify(name)}`;
}

/** Scroll target after marking `completedIndex` done: next incomplete lower in the list, else wrap, else section. */
function getAnchorAfterSubtaskMarkedComplete(
  completedIndex: number,
  nextSub: boolean[],
  subs: TasklistPageSubtask[]
): string {
  const n = Math.min(nextSub.length, subs.length);
  for (let j = completedIndex + 1; j < n; j += 1) {
    if (!nextSub[j]) return subtaskAnchorId(subs[j].name, j);
  }
  for (let j = 0; j <= completedIndex && j < n; j += 1) {
    if (!nextSub[j]) return subtaskAnchorId(subs[j].name, j);
  }
  return WHAT_NEXT_ID;
}

type PlaceholderVars = { studentName: string; school: string; schoolAbbr?: string };

type TasklistSectionRenderCtx = {
  vars: PlaceholderVars;
  linkSources: TasklistLinkSources;
  userRoleVal: string | undefined;
  gradeId: string | undefined;
  studentAge: number | undefined;
  runtime: TasklistDisplayRuntime;
  readOnly: boolean;
};

function formatTasklistPageText(text: string, ctx: TasklistSectionRenderCtx): string {
  return applyTasklistPageContent(text, ctx.vars, ctx.linkSources);
}

function TasklistJsonCtaButton({
  href,
  label,
  className = 'tasklist-json-block-button',
  bare = false,
}: {
  href: string;
  label: string;
  className?: string;
  bare?: boolean;
}): React.ReactElement {
  const btnClass = 'btn btn-lg btn-fill btn-primary btn-icon-after ols_btn';
  const content = (
    <>
      <span className="btn-label">{label}</span>
      <span className="btn-icon" aria-hidden={true}>
        <RedirectIcon />
      </span>
    </>
  );

  const button = isTasklistExternalHref(href) ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={btnClass}>
      {content}
    </a>
  ) : (
    <Link to={href} className={btnClass}>
      {content}
    </Link>
  );

  if (bare) return button;
  return <div className={className}>{button}</div>;
}

function renderTasklistActionLink(
  key: string,
  label: string,
  url: string | undefined,
  readOnly: boolean,
  className: string
): React.ReactNode {
  const text = label.trim();
  if (!text) return null;
  if (readOnly) {
    return (
      <span
        key={key}
        className={`${className} disabled`}
        aria-disabled="true"
        role="button"
      >
        <span className="btn-label">{text}</span>
        <span className="btn-icon" aria-hidden={true}>
          <RedirectIcon />
        </span>
      </span>
    );
  }
  return (
    <a
      key={key}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <span className="btn-label">{text}</span>
      <span className="btn-icon" aria-hidden={true}>
        <RedirectIcon />
      </span>
    </a>
  );
}

function tasklistBlockDataType(block: TasklistPageBlock): string {
  if (block.type === 'accordion') return 'faq';
  return block.type;
}

function TasklistJsonVideo({
  url,
  title,
  className,
}: {
  url: string;
  title?: string;
  className?: string;
}): React.ReactElement {
  const vidyardUuid = parseVidyardUuid(url);
  const classes = ['tasklist-json-video', className].filter(Boolean).join(' ');

  if (vidyardUuid) {
    return (
      <div className={`${classes} video-wrapper`}>
        <VidyardPlayer uuid={vidyardUuid} type="lightbox" />
      </div>
    );
  }

  return (
    <div className={classes}>
      <iframe
        className="tasklist-json-video__iframe"
        src={url}
        title={title || 'Video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      />
    </div>
  );
}

function renderBlock(block: TasklistPageBlock, key: string, ctx: TasklistSectionRenderCtx): React.ReactNode {
  const { linkSources, userRoleVal, gradeId, studentAge, runtime, readOnly } = ctx;
  if (block.type === 'htmlSubsection') {
    if (!shouldShowTasklistSlice(block.displayConditions, userRoleVal, gradeId, runtime, studentAge)) {
      return null;
    }
    return (
      <div key={key} className="tasklist-json-html-subsection">
        <h3 className="heading-3 tasklist-json-html-subsection__title">
          {formatTasklistPageText(block.title, ctx)}
        </h3>
        {block.htmlParts.map((html, hi) => (
          <div
            key={`${key}-sub-${hi}`}
            className="content-block tasklist-json-html-block tasklist-json-html-subsection__body"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ))}
      </div>
    );
  }
  if (block.type === 'accordion') {
    const visible = block.items.filter((item) =>
      shouldShowTasklistSlice(item.displayConditions, userRoleVal, gradeId, runtime, studentAge)
    );
    if (!visible.length) return null;
    /** School overview FAQs: static Q/A list aligned with Replit school-overview document */
    return (
      <div key={key} className="tasklist-json-faq-block">
        <dl className="tasklist-json-faq-list">
          {visible.map((item, ii) => (
            <div key={`${key}-faq-${ii}`} className="tasklist-json-faq-item">
              <dt className="tasklist-json-faq-item__question">
                {formatTasklistPageText(item.title, ctx)}
              </dt>
              <dd className="tasklist-json-faq-item__answer">
                {item.htmlParts.map((html, hi) => (
                  <div
                    key={`${key}-faq-${ii}-h-${hi}`}
                    className="content-block tasklist-json-html-block"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }
  if (block.type === 'text') {
    return (
      <div key={key} className="content-block tasklist-json-block">
        <div className="longform">
          <MarkdownText content={formatTasklistPageText(block.markdown, ctx)} />
        </div>
      </div>
    );
  }
  if (block.type === 'html') {
    return (
      <div
        key={key}
        className="content-block tasklist-json-html-block"
        dangerouslySetInnerHTML={{ __html: formatTasklistPageText(block.html, ctx) }}
      />
    );
  }
  if (block.type === 'callout') {
    return (
      <div key={key} className="tasklist-json-callout content-block my-3 p-3">
        <div className="longform">
          <MarkdownText content={formatTasklistPageText(block.text, ctx)} />
        </div>
      </div>
    );
  }
  if (block.type === 'image') {
    const src = resolveTasklistSubtaskImageSrc(block.filename);
    if (!src) return null;
    const alt = block.alt?.trim() || '';
    return (
      <figure key={key} className="tasklist-json-content-image">
        <ZoomableImage
          src={src}
          alt={alt}
          className="tasklist-json-content-image__zoom"
          imgClassName="tasklist-json-content-image__img"
        />
      </figure>
    );
  }
  if (block.type === 'video') {
    const url = block.videoUrl?.trim();
    if (!url) {
      return null;
    }
    return <TasklistJsonVideo key={key} url={url} title={block.alt || 'Video'} />;
  }
  if (block.type === 'ctaPanel') {
    const ctaHref = resolveTasklistButtonHref(block.buttonUrl, block.linkMeta, linkSources);
    if (!ctaHref) return null;
    return (
      <div key={key} className="tasklist-json-cta-panel">
        {block.title.trim() ? (
          <h3 className="heading-3 tasklist-json-html-subsection__title">
            {formatTasklistPageText(block.title, ctx)}
          </h3>
        ) : null}
        {block.description?.trim() ? (
          <p className="tasklist-json-cta-panel__lead">{formatTasklistPageText(block.description.trim(), ctx)}</p>
        ) : null}
        <div className="tasklist-json-cta-panel__actions">
          {readOnly ? (
            renderTasklistActionLink(
              `${key}-cta`,
              formatTasklistPageText(block.buttonLabel, ctx),
              undefined,
              true,
              'btn btn-lg btn-fill btn-primary btn-icon-after ols_btn'
            )
          ) : (
            <TasklistJsonCtaButton
              href={ctaHref}
              label={formatTasklistPageText(block.buttonLabel, ctx)}
              bare
            />
          )}
        </div>
      </div>
    );
  }
  if (block.type === 'quickLink') {
    const quickHref = resolveTasklistButtonHref(block.buttonUrl, block.linkMeta, linkSources);
    if (!quickHref && !readOnly) return null;
    const heading = block.heading?.trim()
      ? formatTasklistPageText(block.heading, ctx)
      : formatTasklistPageText(block.buttonLabel, ctx);
    const description = block.description?.trim()
      ? formatTasklistPageText(block.description, ctx)
      : undefined;
    const buttonLabel = formatTasklistPageText(block.buttonLabel, ctx);
    if (readOnly) {
      return (
        <div key={key} className="quick-link">
          <h3 className="quick-link__title">{heading}</h3>
          {description ? <p className="quick-link__description">{description}</p> : null}
          {renderTasklistActionLink(
            `${key}-ql`,
            buttonLabel,
            quickHref,
            true,
            'btn btn-lg btn-fill btn-primary btn-icon-after ols_btn quick-link__btn'
          )}
        </div>
      );
    }
    return (
      <QuickLink
        key={key}
        title={heading}
        description={description}
        linkUrl={quickHref}
        buttonText={buttonLabel}
        linkTarget={isTasklistExternalHref(quickHref) ? '_blank' : '_self'}
      />
    );
  }
  if (block.type === 'button') {
    const buttonHref = resolveTasklistButtonHref(block.buttonUrl, block.linkMeta, linkSources);
    if (!buttonHref) return null;
    return (
      <div key={key} className="tasklist-json-block-button">
        {readOnly ? (
          renderTasklistActionLink(
            `${key}-btn`,
            formatTasklistPageText(block.buttonLabel, ctx),
            undefined,
            true,
            'btn btn-lg btn-fill btn-primary btn-icon-after ols_btn'
          )
        ) : (
          <TasklistJsonCtaButton
            href={buttonHref}
            label={formatTasklistPageText(block.buttonLabel, ctx)}
          />
        )}
      </div>
    );
  }
  return null;
}

function renderSection(
  section: TasklistPageSection,
  index: number,
  sectionId: string,
  ctx: TasklistSectionRenderCtx
): React.ReactNode {
  const units = planTasklistSectionRenderUnits(section);
  const quickLinkStack = sectionUsesQuickLinkStack(units);
  const allQuickLinks = units.length > 0 && units.every((u) => u.kind === 'quickLink');

  const blockNodes = units
    .map((unit, ui) => {
      if (unit.kind === 'quickLink') {
        const rendered = renderBlock(unit.quickLink, `${index}-ql-${unit.blockIndex}`, ctx);
        if (!rendered) return null;
        return (
          <div key={`${index}-${ui}-wrap`} data-block-type="quickLink">
            {rendered}
          </div>
        );
      }
      const rendered = renderBlock(unit.block, `${index}-${unit.blockIndex}`, ctx);
      if (!rendered) return null;
      return (
        <div key={`${index}-${ui}-wrap`} data-block-type={tasklistBlockDataType(unit.block)}>
          {rendered}
        </div>
      );
    })
    .filter((node): node is React.ReactElement => node != null);

  let body: React.ReactNode = null;
  if (quickLinkStack) {
    body = <div className="tasklist-json-quick-links">{blockNodes}</div>;
  } else if (allQuickLinks && blockNodes.length === 1) {
    body = blockNodes[0];
  } else if (blockNodes.length > 0) {
    body = <div className="longform tasklist-json-longform-stack">{blockNodes}</div>;
  }

  return (
    <section
      key={sectionId}
      id={sectionId}
      className="tasklist-json-anchor tasklist-json-section tasklist-json-section-card"
    >
      <h2 className="heading-2">{formatTasklistPageText(section.title, ctx)}</h2>
      {body}
    </section>
  );
}

const TasklistJsonPage: React.FC = () => {
  const { pageId: pageIdFromRoute } = useParams<{ pageId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { userRoleVal: accountRoleVal, identityId: viewerIdentityId, loggedInUser } = useOverviewContext();
  const userRoleVal = studentPreviewRole(accountRoleVal);
  const teacherPreview = isTeacherPreview(accountRoleVal);
  const { selectedStudent } = useSelectedStudent();
  const {
    learningCenterTasks,
    tasklistData,
    heroTasks,
    patchLearningCenterTaskCompletion,
    refreshLearningCenterTaskCompletionFromApi,
  } = useTasklist();
  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken ?? '';
  const identityId = selectedStudent?.identityId ?? '';

  useEffect(() => {
    void preloadCompletionCelebrationImages();
  }, []);

  /** Acting user relay `PUT …/tasks/complete` bodies. */
  const subtaskCompleteActor = useMemo((): SubtaskCompleteActor | null => {
    const userIdentityId = Number(viewerIdentityId);
    let userAccountId = Number(
      loggedInUser?.accountId ?? loggedInUser?.userAccountId ?? loggedInUser?.accountID
    );
    if (!Number.isFinite(userAccountId) || userAccountId <= 0) {
      userAccountId = Number(selectedStudent?.accountId);
    }
    if (!Number.isFinite(userIdentityId) || userIdentityId <= 0) return null;
    if (!Number.isFinite(userAccountId) || userAccountId <= 0) return null;
    return { userIdentityId, userAccountId };
  }, [viewerIdentityId, loggedInUser, selectedStudent?.accountId]);

  const isTasklistLayout = location.pathname.includes('/tasklist/');
  /** Canonical CMS `pageId` (for JSON + APIs); `/tasklist/page/{slug}` resolves via `learningCenterTasks`. */
  const pageId = useMemo(() => {
    const raw = pageIdFromRoute?.trim();
    if (!raw) return undefined;
    if (isTasklistLayout) {
      return resolveLearningCenterRouteParam(raw, learningCenterTasks);
    }
    return resolveHeroTaskPageRouteParam(raw, tasklistData);
  }, [pageIdFromRoute, isTasklistLayout, learningCenterTasks, tasklistData]);
  const headerVariant: TasklistJsonPageHeaderVariant = isTasklistLayout ? 'tasklist' : 'task';
  const { data: schoolData, loading: schoolLoading } = useSchool();
  const { schoolLink, myAccountLink, k12ZoneLink } = useLinksContext();
  const { runtime: tasklistDisplayRuntime, isReady: displayRuntimeReady, k12SchoolHref } =
    useTasklistDisplayRuntime();
  const { visibleHeroTasks, visibleLearningCenterTasks } = useFilteredTasklistTasks();
  const [loadError, setLoadError] = useState(false);
  const [fetchedPageJson, setFetchedPageJson] = useState<TasklistPageJson | null>(null);
  const [hasEnrolledFlex, setHasEnrolledFlex] = useState(false);

  const wpPagePayload = useMemo(() => {
    if (!pageId) return undefined;
    const pages = schoolData?.onboarding_content?.checklist_secondary_pages;
    if (!pages || typeof pages !== 'object') return undefined;
    return (pages as Record<string, unknown>)[pageId];
  }, [pageId, schoolData?.onboarding_content?.checklist_secondary_pages]);

  const isPrivateSchool = useMemo(
    () => config.app.privateSchoolIds.includes(selectedStudent?.schoolId ?? ''),
    [selectedStudent?.schoolId]
  );

  const finalizePageJson = useCallback(
    (json: TasklistPageJson): TasklistPageJson => {
      if (
        pageId === '400' &&
        schoolData?.onboarding_content &&
        Array.isArray(json.wpSections) &&
        json.wpSections.length > 0
      ) {
        return mergeSchoolOverviewPageFromWp(json, schoolData, {
          isPrivateSchool,
          hasEnrolledFlex,
          userRoleVal,
        });
      }
      return json;
    },
    [pageId, schoolData, isPrivateSchool, hasEnrolledFlex, userRoleVal]
  );

  /** WP / school-overview JSON is available synchronously — avoids a loading skeleton flash. */
  const syncPageJson = useMemo((): TasklistPageJson | null => {
    if (!pageId || !/^[a-zA-Z0-9_-]+$/.test(pageId)) return null;

    const fromWp = parseTasklistPageFromWpPayload(wpPagePayload);
    if (fromWp) return finalizePageJson(fromWp);

    if (pageId === '400' && schoolData?.onboarding_content) {
      return buildSchoolOverviewPageFromOnboarding(schoolData, {
        isPrivateSchool,
        hasEnrolledFlex,
        userRoleVal,
        quickLinks: {
          calendarLink: schoolData.calendar_link,
          schoolAbbreviation: schoolData.abbreviation,
        },
      });
    }
    return null;
  }, [pageId, wpPagePayload, schoolData, isPrivateSchool, hasEnrolledFlex, userRoleVal, finalizePageJson]);

  const data = syncPageJson ?? fetchedPageJson;

  const placeholderVars = useMemo<PlaceholderVars>(
    () => ({
      studentName: selectedStudent?.nickName || selectedStudent?.firstName || 'your student',
      school: selectedStudent?.schoolShortName || selectedStudent?.schoolName || 'your school',
      schoolAbbr:
        schoolData?.abbreviation?.trim() ||
        selectedStudent?.schoolShortName?.trim() ||
        selectedStudent?.schoolName?.trim() ||
        undefined,
    }),
    [selectedStudent, schoolData?.abbreviation]
  );

  const linkSources = useMemo(
    () =>
      buildTasklistLinkSources({
        schoolLinkUrl: schoolLink.schoolLinkUrl,
        k12SchoolHref,
        myAccountLink,
        k12ZoneLink,
        calendarLink: schoolData?.calendar_link ?? null,
        userRoleVal,
      }),
    [schoolLink, k12SchoolHref, myAccountLink, k12ZoneLink, schoolData?.calendar_link, userRoleVal]
  );

  const gradeId = useMemo(
    () => tasklistDisplayGradeId(selectedStudent?.gradeId, accountRoleVal),
    [selectedStudent?.gradeId, accountRoleVal]
  );

  const studentAge = selectedStudent?.age;

  const tasklistSectionRenderCtx = useMemo<TasklistSectionRenderCtx>(
    () => ({
      vars: placeholderVars,
      linkSources,
      userRoleVal,
      gradeId,
      studentAge,
      runtime: tasklistDisplayRuntime,
      readOnly: teacherPreview,
    }),
    [placeholderVars, linkSources, userRoleVal, gradeId, studentAge, tasklistDisplayRuntime, teacherPreview]
  );

  const formatPageText = useCallback(
    (text: string) => applyTasklistPageContent(text, placeholderVars, linkSources),
    [placeholderVars, linkSources]
  );

  const visibleHeroPageIds = useMemo(
    () =>
      new Set(
        visibleHeroTasks
          .map((t) => t.pageId?.trim())
          .filter((pid): pid is string => Boolean(pid))
      ),
    [visibleHeroTasks]
  );

  const completionModalNext = useMemo(
    () =>
      getCompletionModalNext(
        pageId,
        tasklistData,
        heroTasks,
        visibleLearningCenterTasks,
        visibleHeroPageIds
      ),
    [pageId, tasklistData, heroTasks, visibleLearningCenterTasks, visibleHeroPageIds]
  );

  const learningCenterTaskForPage = useMemo(() => {
    if (!pageId?.trim()) return undefined;
    const pid = pageId.trim();
    return learningCenterTasks.find((t) => (t.pageId ?? '').trim() === pid);
  }, [pageId, learningCenterTasks]);

  const learningCenterTaskId = learningCenterTaskForPage?.id;
  /** Backend GET `/tasks` uses CMS ids (`pageId` / LC task id); hero may override list parent via {@link tasklistHeroRowForPage.subtasksCmsParentId}. */
  const learningCenterBackendTaskId = useMemo(
    () =>
      learningCenterTaskForPage
        ? resolveLearningCenterBackendTaskId(learningCenterTaskForPage)
        : undefined,
    [learningCenterTaskForPage]
  );
  /** Id sent to task/subtask completion APIs (matches autocomplete `taskId`, often === `pageId`). */
  const lcApiTaskIdForPut = useMemo(
    () => learningCenterBackendTaskId ?? learningCenterTaskId,
    [learningCenterBackendTaskId, learningCenterTaskId]
  );
  const tasklistHeroRowForPage = useMemo(() => {
    const pid = pageId?.trim();
    if (!pid) return undefined;
    return tasklistData.find((c) => (c.pageId ?? '').trim() === pid);
  }, [pageId, tasklistData]);

  /** `PUT /tasks/complete` + `/tasks` autocomplete — numeric `pageId` or LC `taskId`, not `subtasksCmsParentId`. */
  const relayTaskIdForParentCompletion = useMemo((): number | undefined => {
    if (lcApiTaskIdForPut != null && Number.isFinite(Number(lcApiTaskIdForPut))) {
      return Number(lcApiTaskIdForPut);
    }
    const raw = String(pageId ?? '').trim();
    if (!raw) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }, [lcApiTaskIdForPut, pageId]);

  /**
   * Parent id used when grouping **`type:"subtask"`** rows from GET **`/tasks`** for this checklist page — for hero
   * pages, CMS may use a different id than static JSON (`tasklist.json` `subtasksCmsParentId`).
   */
  const relayTaskIdForSubtasksList = useMemo((): number | undefined => {
    if (lcApiTaskIdForPut != null && Number.isFinite(Number(lcApiTaskIdForPut))) {
      return Number(lcApiTaskIdForPut);
    }
    const cms = tasklistHeroRowForPage?.subtasksCmsParentId;
    if (cms != null && Number.isFinite(Number(cms))) {
      return Number(cms);
    }
    return relayTaskIdForParentCompletion;
  }, [lcApiTaskIdForPut, tasklistHeroRowForPage?.subtasksCmsParentId, relayTaskIdForParentCompletion]);

  /** Parent task PUT `/tasks/complete` when we know `taskId`, student `identityId`, and acting user for the body. */
  const canCallTaskCompletionApi = useMemo(
    () =>
      !teacherPreview &&
      Boolean(
        accessToken &&
          identityId &&
          relayTaskIdForParentCompletion != null &&
          relayTaskIdForSubtasksList != null &&
          subtaskCompleteActor
      ),
    [
      teacherPreview,
      accessToken,
      identityId,
      relayTaskIdForParentCompletion,
      relayTaskIdForSubtasksList,
      subtaskCompleteActor,
    ]
  );

  const useLcProgressApi = Boolean(learningCenterTaskId != null && accessToken && identityId);

  /**
   * Avoid writing localStorage when this page syncs to onboarding v2 (Learning Center or hero `group: tasks` with numeric `pageId`).
   */
  const persistTasklistPageProgressToLocalOnly = useMemo(
    () => !useLcProgressApi && !canCallTaskCompletionApi,
    [useLcProgressApi, canCallTaskCompletionApi]
  );

  /**
   * Hero checklist pages (e.g. Setup and Access) live in `tasklist.json` `tasks`, not `learningCenterTasks`.
   * Subtask sync still used `learningCenterTaskForPage?.completed` only — always false for those pages —
   * so after `/tasks` showed complete, we could fall back to stale localStorage and show subtasks incomplete.
   */
  const parentTaskCompletedFromServer = useMemo(() => {
    if (Boolean(learningCenterTaskForPage?.completed)) return true;
    const pid = pageId?.trim();
    if (!pid) return false;
    const row = tasklistData.find((c) => (c.pageId ?? '').trim() === pid);
    if (!row) return false;
    const hero = heroTasks.find((h) => Number(h.id) === Number(row.checklistId));
    return Boolean(hero?.completed);
  }, [learningCenterTaskForPage?.completed, pageId, tasklistData, heroTasks]);

  const syncLearningCenterHomeWithServer = useCallback(async () => {
    if (!accessToken || !identityId) return;
    await refreshLearningCenterTaskCompletionFromApi();
  }, [accessToken, identityId, refreshLearningCenterTaskCompletionFromApi]);

  const visibleKeepLearningItems = useMemo(() => {
    if (!displayRuntimeReady) return [];
    return getVisibleKeepLearningEntries(
      data?.keepLearning,
      userRoleVal,
      gradeId,
      tasklistDisplayRuntime,
      studentAge
    );
  }, [data?.keepLearning, userRoleVal, gradeId, tasklistDisplayRuntime, displayRuntimeReady, studentAge]);

  const visibleSubtasks = useMemo(() => {
    if (!data || !displayRuntimeReady) return [];
    return getVisibleSubtasks(data.subtasks, userRoleVal, gradeId, tasklistDisplayRuntime, studentAge);
  }, [data, userRoleVal, gradeId, tasklistDisplayRuntime, displayRuntimeReady, studentAge]);

  const pageRootClass = useMemo(
    () =>
      `setup-your-accounts tasklist-json-page${isTasklistLayout ? ' tasklist-json-page--tasklist' : ''}${
        teacherPreview
          ? ' tasklist-json-page--teacher-preview'
          : !data
            ? ''
            : visibleSubtasks.length > 0
              ? ' tasklist-json-page--has-task-footer'
              : ' tasklist-json-page--has-mark-read-footer'
      }`,
    [isTasklistLayout, visibleSubtasks.length, data, teacherPreview]
  );

  const visibleContentSections = useMemo(() => {
    if (!displayRuntimeReady) return [];
    return getVisibleContentSections(
      data?.content,
      userRoleVal,
      gradeId,
      tasklistDisplayRuntime,
      studentAge
    );
  }, [data?.content, userRoleVal, gradeId, tasklistDisplayRuntime, displayRuntimeReady, studentAge]);

  const isPageVisibleForUser = useMemo(() => {
    if (!data || !displayRuntimeReady) return false;
    return shouldShowTasklistSlice(
      data.displayConditions,
      userRoleVal,
      gradeId,
      tasklistDisplayRuntime,
      studentAge
    );
  }, [data, displayRuntimeReady, userRoleVal, gradeId, tasklistDisplayRuntime, studentAge]);

  const subtaskListItems: SubTaskListItem[] = useMemo(
    () =>
      visibleSubtasks.map((st, i) => {
        const subtaskImageSrc = resolveTasklistSubtaskImageSrc(st.image);
        const videoUrl = st.mediaType === 'video' ? st.video?.trim() : '';
        const descriptionBlock = (
          <div className="content-block tasklist-json-subtask-details">
            <div className="longform">
              <MarkdownText content={formatPageText(st.description)} />
            </div>
          </div>
        );
        const mediaBlock =
          videoUrl ? (
            <TasklistJsonVideo
              url={videoUrl}
              title={st.name || 'Video'}
              className="tasklist-json-subtask-media"
            />
          ) : subtaskImageSrc ? (
            <figure className="tasklist-json-subtask-media">
              <ZoomableImage
                src={subtaskImageSrc}
                alt={st.caption || st.name}
                className="tasklist-json-subtask-zoom"
                imgClassName="tasklist-json-subtask-zoom__img"
              />
            </figure>
          ) : st.caption ? (
            <div className="tasklist-json-subtask-media tasklist-json-subtask-media--placeholder">
              <span className="tasklist-json-subtask-media-placeholder-label">Screenshot: {st.caption}</span>
            </div>
          ) : null;

        return {
          id: `st-${i}`,
          label: st.name,
          anchorId: subtaskAnchorId(st.name, i),
          details: (
            <>
              {descriptionBlock}
              {mediaBlock}
            </>
          ),
        };
      }),
    [visibleSubtasks, formatPageText]
  );

  const tocItems = useMemo(() => {
    const items: { id: string; label: string }[] = [];
    visibleContentSections.forEach((s, i) => {
      items.push({
        id: sectionAnchorId(s, i),
        label: formatPageText(s.title),
      });
    });
    if (visibleSubtasks.length > 0) {
      items.push({ id: WHAT_NEXT_ID, label: 'What To Do Next' });
    }
    if (visibleKeepLearningItems.length > 0) {
      items.push({ id: KEEP_LEARNING_ID, label: 'Keep Learning' });
    }
    return items;
  }, [visibleContentSections, visibleSubtasks, visibleKeepLearningItems, formatPageText]);

  const tocIdsKey = useMemo(() => tocItems.map((i) => i.id).join('|'), [tocItems]);
  const firstTocId = tocItems[0]?.id ?? null;

  const [activeId, setActiveId] = useState<string | null>(null);
  /** Keeps clicked TOC item highlighted until smooth scroll reaches the target section. */
  const tocNavLockRef = useRef<string | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const tocScrollRafRef = useRef<number | null>(null);
  const tocLockClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    tocNavLockRef.current = null;
    const hashId = location.hash.replace(/^#/, '');
    const hashInToc = Boolean(hashId && tocItems.some((item) => item.id === hashId));
    const next = isPageVisibleForUser ? (hashInToc ? hashId : firstTocId) : null;
    if (activeIdRef.current === next) return;
    activeIdRef.current = next;
    setActiveId(next);
  }, [pageId, tocIdsKey, isPageVisibleForUser, firstTocId, location.hash, tocItems]);

  const [tasklistProgress, setTasklistProgress] = useState<TasklistPageProgress>({
    subtasks: [],
    taskMarkedComplete: false,
  });
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const subtaskApiIdsRef = useRef<number[]>([]);
  /**
   * After a local subtask PUT, `/tasks` autocomplete can briefly lag — without this, we coerce merged subs to complete
   * when the parent row is complete on Overview and incorrectly wipe partial unchecks.
   */
  const skipSubtaskMergeCoerceUntilRef = useRef(0);
  /**
   * Subtask toggles update the server via PUT but intentionally do not refresh the session snapshot.
   * While dirty, skip re-merging from session so stale snapshot rows cannot overwrite local checkbox state.
   */
  const subtaskStateDirtyVsSnapshotRef = useRef(false);
  /** Clears dirty when the user navigates to a different task page / student / history entry. */
  const layoutSnapshotKeyRef = useRef('');
  /** Last layout + page identity for merging stored `/tasks` snapshot into this page's subtasks. */
  const lastTasksFetchKeyRef = useRef('');
  /** Latest `/tasks` payload for this student (session mirror or last explicit sync — never fetched inside this effect). */
  const lastFetchedTasksPayloadRef = useRef<unknown | null>(null);
  const [subtaskSyncInFlightIndex, setSubtaskSyncInFlightIndex] = useState<number | null>(null);
  /** Latest `/tasks` payload for this student (session + in-memory; this effect does not call GET `/tasks`). */
  const [, setTasksSnapshot] = useState<unknown | null>(null);
  /** Prevents duplicate Mark Complete submissions while a background sync is in flight. */
  const markCompleteSyncInflightRef = useRef(false);
  /** Prevents duplicate unmark / mark-read syncs while background API runs. */
  const taskFooterSyncInflightRef = useRef(false);

  const subtaskCompleted = tasklistProgress.subtasks;

  useEffect(() => {
    setCelebrationOpen(false);
    setCompletionModalOpen(false);
  }, [pageId]);

  const visibleSubtaskNamesKey = useMemo(
    () => visibleSubtasks.map((s) => s.name).join('\u0001'),
    [visibleSubtasks]
  );

  useEffect(() => {
    if (!data || !pageId) return;
    const n = visibleSubtasks.length;

    /** Without auth or a parent task id suitable for grouping GET `/tasks` subtasks, keep local-only progress. */
    if (!accessToken || !identityId || relayTaskIdForSubtasksList == null) {
      if (n === 0) {
        setTasklistProgress(loadTasklistPageProgress(selectedStudent?.accountId, pageId, 0));
      } else {
        const loaded = loadTasklistPageProgress(selectedStudent?.accountId, pageId, n);
        if (parentTaskCompletedFromServer && !loaded.subtasks.every(Boolean)) {
          setTasklistProgress({
            subtasks: Array(n).fill(true),
            taskMarkedComplete: parentTaskCompletedFromServer,
          });
        } else {
          setTasklistProgress(loaded);
        }
      }
      return;
    }

    const layoutSnapshotKey = `${location.key}|${identityId}|${pageId ?? ''}|${String(
      relayTaskIdForSubtasksList ?? ''
    )}`;
    const tasksFetchKey = `${layoutSnapshotKey}|${visibleSubtaskNamesKey}`;
    if (layoutSnapshotKeyRef.current !== layoutSnapshotKey) {
      layoutSnapshotKeyRef.current = layoutSnapshotKey;
      subtaskStateDirtyVsSnapshotRef.current = false;
    }

    if (n === 0) {
      subtaskApiIdsRef.current = [];
      setTasklistProgress({ subtasks: [], taskMarkedComplete: parentTaskCompletedFromServer });
      return;
    }

    if (subtaskStateDirtyVsSnapshotRef.current) {
      return () => {};
    }

    let cancelled = false;

    const applyMerged = (merged: { subtasks: boolean[]; subTaskIds: number[] }) => {
      if (cancelled) return;
      let subtasks = merged.subtasks;
      const recentLocalSubtaskEdit =
        typeof performance !== 'undefined' &&
        performance.now() < skipSubtaskMergeCoerceUntilRef.current;
      if (
        parentTaskCompletedFromServer &&
        n > 0 &&
        subtasks.length === n &&
        !subtasks.every(Boolean) &&
        !recentLocalSubtaskEdit
      ) {
        /** `/tasks` autocomplete (home) can be ahead of subtask title-merge or empty GET — match Overview. */
        subtasks = Array(n).fill(true);
      }
      subtaskApiIdsRef.current = merged.subTaskIds;
      const allDone = subtasks.length > 0 && subtasks.every(Boolean);
      /**
       * Full subtask checkmarks ≠ user tapped “Mark Task Complete”. Keep the footer CTA until confirm
       * unless the parent task is already complete on the server (Learning Center merge).
       */
      setTasklistProgress({
        subtasks,
        taskMarkedComplete: allDone ? parentTaskCompletedFromServer : false,
      });
    };

    const fallbackLocalOnly = () => {
      if (cancelled) return;
      if (parentTaskCompletedFromServer && n > 0) {
        applyMerged({ subtasks: Array(n).fill(true), subTaskIds: Array(n).fill(0) });
        return;
      }
      subtaskApiIdsRef.current = [];
      setTasklistProgress(loadTasklistPageProgress(selectedStudent?.accountId, pageId, n));
    };

    const mergePayloadIntoProgress = (payload: unknown): void => {
      try {
        const autoRows = tasksPayloadSubtasksForParent(payload, relayTaskIdForSubtasksList!);
        if (autoRows.length === 0) {
          fallbackLocalOnly();
          return;
        }
        const rowsForMerge = withFallbackSubtaskTitlesForMerge(autoRows, visibleSubtasks);
        const enriched = mapOnboardingPageSubtasksFromApi(data, rowsForMerge);
        const visibleEnriched = getVisibleSubtasks(
          enriched.subtasks ?? [],
          userRoleVal,
          gradeId,
          tasklistDisplayRuntime,
          studentAge
        );
        const apiForMerge = autoRows.map((r) => ({
          subTaskId: r.taskId,
          completed: r.completed,
        }));
        const merged = mergeSubtaskCompletionFromApi(
          enriched.subtasks ?? [],
          visibleEnriched,
          apiForMerge
        );
        applyMerged(merged);
      } catch (err) {
        console.warn('Tasks snapshot merge failed; using local state.', err);
        if (!cancelled) fallbackLocalOnly();
      }
    };

    /**
     * Merge subtasks from the stored GET `/tasks` snapshot for this student only (sessionStorage).
     * This effect never calls the Tasks API — data is primed elsewhere (Overview, ChecklistContext, Mark Complete).
     */
    const loadSubtasksFromTasksRelay = (): void => {
      const hydrateFromSessionSnapshot = (): boolean => {
        const cached = readTasksSnapshotJson(identityId);
        if (cached == null) return false;
        lastFetchedTasksPayloadRef.current = cached;
        setTasksSnapshot(cached);
        mergePayloadIntoProgress(cached);
        return true;
      };

      const keyChanged = lastTasksFetchKeyRef.current !== tasksFetchKey;
      if (keyChanged) {
        lastTasksFetchKeyRef.current = tasksFetchKey;
        if (!hydrateFromSessionSnapshot()) {
          lastFetchedTasksPayloadRef.current = null;
          fallbackLocalOnly();
        }
      } else if (lastFetchedTasksPayloadRef.current != null) {
        mergePayloadIntoProgress(lastFetchedTasksPayloadRef.current);
      } else if (!hydrateFromSessionSnapshot()) {
        fallbackLocalOnly();
      }
    };

    loadSubtasksFromTasksRelay();

    return () => {
      cancelled = true;
    };
  }, [
    data,
    pageId,
    selectedStudent?.accountId,
    visibleSubtaskNamesKey,
    visibleSubtasks,
    accessToken,
    identityId,
    relayTaskIdForSubtasksList,
    parentTaskCompletedFromServer,
    userRoleVal,
    gradeId,
    tasklistDisplayRuntime,
    location.key,
  ]);

  const handleTocNavigate = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    tocNavLockRef.current = id;
    if (activeIdRef.current !== id) {
      activeIdRef.current = id;
      setActiveId(id);
    }
    window.history.replaceState(null, '', `#${id}`);
  }, []);

  useEffect(() => {
    if (!isPageVisibleForUser) return;
    const hashId = location.hash.replace(/^#/, '');
    if (!hashId) return;

    let cancelled = false;
    const tryNavigate = () => {
      if (cancelled) return;
      if (document.getElementById(hashId)) {
        handleTocNavigate(hashId);
      }
    };

    tryNavigate();
    const raf = requestAnimationFrame(tryNavigate);
    const t1 = window.setTimeout(tryNavigate, 150);
    const t2 = window.setTimeout(tryNavigate, 400);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.hash, location.key, isPageVisibleForUser, tocIdsKey, handleTocNavigate]);

  const syncTocActiveFromScroll = useCallback(() => {
    if (!tocItems.length) return;
    const { activeId: nextActive, releaseLock } = syncTocScrollSpyState(
      tocItems,
      tocNavLockRef.current
    );
    if (releaseLock) {
      tocNavLockRef.current = null;
    }
    if (nextActive && nextActive !== activeIdRef.current) {
      activeIdRef.current = nextActive;
      setActiveId(nextActive);
    }
  }, [tocItems]);

  const scrollToNextIncompleteSubtask = useCallback(() => {
    handleTocNavigate(getAnchorAfterSubtaskMarkedComplete(-1, subtaskCompleted, visibleSubtasks));
  }, [visibleSubtasks, subtaskCompleted, handleTocNavigate]);

  const handleToggleSubtask = useCallback(
    async (index: number) => {
      if (teacherPreview) return;
      const n = visibleSubtasks.length;
      if (n === 0) return;

      if (index < 0 || index >= n) return;

      const subId = subtaskApiIdsRef.current[index];

      /** Same onboarding v2 PUTs as Learning Center when we have parent task id + actor (includes hero `group: tasks` pages). */
      if (!canCallTaskCompletionApi || !subId) {
        logSubtaskToggleSkippedApiDev({
          rowIndex: index,
          canCallTaskCompletionApi,
          subId,
          accessToken,
          identityId,
          relayTaskIdForSubtasksList,
          relayTaskIdForParentCompletion,
          subtaskCompleteActor,
        });
        setTasklistProgress((prev) => {
          const base =
            prev.subtasks.length === n
              ? [...prev.subtasks]
              : loadTasklistPageProgress(selectedStudent?.accountId, pageId, n).subtasks;
          const nextSub = [...base];
          nextSub[index] = !nextSub[index];
          const allDone = nextSub.length > 0 && nextSub.every(Boolean);
          const next: TasklistPageProgress = {
            subtasks: nextSub,
            taskMarkedComplete: allDone ? prev.taskMarkedComplete : false,
          };
          if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
            saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
          }
          if (nextSub[index]) {
            const targetId = getAnchorAfterSubtaskMarkedComplete(index, nextSub, visibleSubtasks);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                handleTocNavigate(targetId);
              });
            });
          }
          return next;
        });
        return;
      }

      const nextComplete = !subtaskCompleted[index];

      setSubtaskSyncInFlightIndex(index);
      try {
        await putSubtaskComplete(
          accessToken,
          identityId,
          relayTaskIdForParentCompletion!,
          subId,
          nextComplete,
          subtaskCompleteActor!
        );
        const base =
          subtaskCompleted.length === n
            ? [...subtaskCompleted]
            : loadTasklistPageProgress(selectedStudent?.accountId, pageId, n).subtasks;
        const nextSub = [...base];
        nextSub[index] = nextComplete;
        const allDone = nextSub.length > 0 && nextSub.every(Boolean);
        setTasklistProgress((prev) => ({
          subtasks: nextSub,
          taskMarkedComplete: allDone ? prev.taskMarkedComplete : false,
        }));
        if (nextComplete) {
          const targetId = getAnchorAfterSubtaskMarkedComplete(index, nextSub, visibleSubtasks);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              handleTocNavigate(targetId);
            });
          });
        }
        /**
         * Mark parent incomplete on the server only when it is still complete on Overview.
         * Repeating `PUT /tasks/complete` false while the parent is already incomplete can cause
         * gateways/CMS to reset multiple subtasks at once (user sees more than one row uncheck).
         */
        if (!allDone && parentTaskCompletedFromServer) {
          await putTaskComplete(
            accessToken,
            identityId,
            relayTaskIdForParentCompletion!,
            false,
            subtaskCompleteActor!
          );
        }
        /** Keep GET `/tasks` session snapshot aligned without a network round-trip (see useEffect comment). */
        const priorPayload = lastFetchedTasksPayloadRef.current ?? readTasksSnapshotJson(identityId);
        const localPatch: LocalTasksSnapshotPatch = {
          subtask: { taskId: subId, completed: nextComplete },
        };
        if (!allDone && parentTaskCompletedFromServer) {
          localPatch.parentTask = {
            taskId: relayTaskIdForParentCompletion!,
            completed: false,
          };
        }
        const patchedSnapshot = cloneTasksPayloadWithLocalCompletionPatches(priorPayload, localPatch);
        if (patchedSnapshot) {
          writeTasksSnapshotFromJson(identityId, patchedSnapshot);
          lastFetchedTasksPayloadRef.current = patchedSnapshot;
          setTasksSnapshot(patchedSnapshot);
        } else if (import.meta.env.DEV) {
          console.warn(
            '[onboarding] Subtask relay succeeded but tasks snapshot could not be patched locally; subtask state may not match session until the next Tasks snapshot refresh.'
          );
        }
        /** Parent `type: task` completion is only sent when the user taps Mark Complete (`handleConfirmTaskDone`). */
        if (learningCenterTaskId != null && !allDone) {
          patchLearningCenterTaskCompletion(learningCenterTaskId, false);
        }
        subtaskStateDirtyVsSnapshotRef.current = true;
      } catch (e) {
        console.error('Subtask completion update failed:', e);
      } finally {
        if (typeof performance !== 'undefined') {
          skipSubtaskMergeCoerceUntilRef.current = performance.now() + 3500;
        }
        setSubtaskSyncInFlightIndex((current) => (current === index ? null : current));
      }
    },
    [
      accessToken,
      identityId,
      canCallTaskCompletionApi,
      relayTaskIdForParentCompletion,
      relayTaskIdForSubtasksList,
      learningCenterTaskId,
      pageId,
      parentTaskCompletedFromServer,
      patchLearningCenterTaskCompletion,
      persistTasklistPageProgressToLocalOnly,
      selectedStudent?.accountId,
      subtaskCompleted,
      visibleSubtasks,
      handleTocNavigate,
      subtaskCompleteActor,
      teacherPreview,
    ]
  );

  const handleConfirmTaskDone = useCallback(() => {
    if (teacherPreview) return;
    const n = visibleSubtasks.length;
    const showCelebration =
      n > 0 &&
      subtaskCompleted.length === n &&
      subtaskCompleted.length > 0 &&
      subtaskCompleted.every(Boolean);

    if (markCompleteSyncInflightRef.current) return;
    markCompleteSyncInflightRef.current = true;

    setTasklistProgress((prev) => {
      const next = { ...prev, taskMarkedComplete: true };
      if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
        saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
      }
      return next;
    });
    if (learningCenterTaskId != null) {
      patchLearningCenterTaskCompletion(learningCenterTaskId, true);
    }
    if (showCelebration) {
      setCelebrationOpen(true);
    }

    void (async () => {
      try {
        if (!canCallTaskCompletionApi) return;
        await putTaskComplete(
          accessToken,
          identityId,
          relayTaskIdForParentCompletion!,
          true,
          subtaskCompleteActor!
        );
        const tasksPayload = await fetchTasksSnapshotAndPersist(accessToken, identityId);
        setTasksSnapshot(tasksPayload);
        lastFetchedTasksPayloadRef.current = tasksPayload;
        subtaskStateDirtyVsSnapshotRef.current = false;
        await refreshLearningCenterTaskCompletionFromApi();
      } catch (e) {
        console.error('Learning Center task completion update failed:', e);
        setTasklistProgress((prev) => {
          const next = { ...prev, taskMarkedComplete: false };
          if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
            saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
          }
          return next;
        });
        if (learningCenterTaskId != null) {
          patchLearningCenterTaskCompletion(learningCenterTaskId, false);
        }
        try {
          await refreshLearningCenterTaskCompletionFromApi();
        } catch {
          /* ignore */
        }
      } finally {
        markCompleteSyncInflightRef.current = false;
      }
    })();
  }, [
    accessToken,
    identityId,
    canCallTaskCompletionApi,
    relayTaskIdForParentCompletion,
    subtaskCompleteActor,
    pageId,
    patchLearningCenterTaskCompletion,
    persistTasklistPageProgressToLocalOnly,
    refreshLearningCenterTaskCompletionFromApi,
    selectedStudent?.accountId,
    subtaskCompleted,
    visibleSubtasks.length,
    learningCenterTaskId,
    teacherPreview,
  ]);

  const handleCelebrationDismiss = useCallback(async () => {
    if (teacherPreview) return;
    await syncLearningCenterHomeWithServer();
    setCelebrationOpen(false);
    setCompletionModalOpen(true);
  }, [syncLearningCenterHomeWithServer, teacherPreview]);

  const handleCompletionModalDismiss = useCallback(async () => {
    if (teacherPreview) return;
    setCompletionModalOpen(false);
    await syncLearningCenterHomeWithServer();
  }, [syncLearningCenterHomeWithServer, teacherPreview]);

  const handleCompletionKeepLearning = useCallback(async () => {
    if (teacherPreview) return;
    setCompletionModalOpen(false);
    await syncLearningCenterHomeWithServer();
    if (completionModalNext.hasNext && completionModalNext.nextPageId) {
      const path = isTasklistLayout
        ? tasklistLearningCenterPagePath(completionModalNext.nextPageId, learningCenterTasks)
        : taskHeroPagePath(completionModalNext.nextPageId, tasklistData);
      navigate(path);
    } else {
      navigate('/overview');
    }
  }, [completionModalNext, isTasklistLayout, learningCenterTasks, tasklistData, navigate, syncLearningCenterHomeWithServer, teacherPreview]);

  const handleMarkTaskIncomplete = useCallback(() => {
    if (teacherPreview) return;
    if (taskFooterSyncInflightRef.current) return;
    taskFooterSyncInflightRef.current = true;

    setCelebrationOpen(false);
    setCompletionModalOpen(false);

    setTasklistProgress((prev) => {
      const next = { ...prev, taskMarkedComplete: false };
      if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
        saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
      }
      return next;
    });
    if (learningCenterTaskId != null) {
      patchLearningCenterTaskCompletion(learningCenterTaskId, false);
    }

    void (async () => {
      try {
        if (!canCallTaskCompletionApi) return;
        await putTaskComplete(
          accessToken,
          identityId,
          relayTaskIdForParentCompletion!,
          false,
          subtaskCompleteActor!
        );
        const tasksPayload = await fetchTasksSnapshotAndPersist(accessToken, identityId);
        setTasksSnapshot(tasksPayload);
        lastFetchedTasksPayloadRef.current = tasksPayload;
        await refreshLearningCenterTaskCompletionFromApi();
      } catch (e) {
        console.error('Learning Center task completion update failed:', e);
        setTasklistProgress((prev) => {
          const next = { ...prev, taskMarkedComplete: true };
          if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
            saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
          }
          return next;
        });
        if (learningCenterTaskId != null) {
          patchLearningCenterTaskCompletion(learningCenterTaskId, true);
        }
        try {
          await refreshLearningCenterTaskCompletionFromApi();
        } catch {
          /* ignore */
        }
      } finally {
        taskFooterSyncInflightRef.current = false;
      }
    })();
  }, [
    accessToken,
    identityId,
    canCallTaskCompletionApi,
    relayTaskIdForParentCompletion,
    subtaskCompleteActor,
    learningCenterTaskId,
    pageId,
    patchLearningCenterTaskCompletion,
    persistTasklistPageProgressToLocalOnly,
    refreshLearningCenterTaskCompletionFromApi,
    selectedStudent?.accountId,
    teacherPreview,
  ]);

  const handleMarkRead = useCallback(() => {
    if (teacherPreview) return;
    if (visibleSubtasks.length !== 0) return;
    if (taskFooterSyncInflightRef.current) return;
    taskFooterSyncInflightRef.current = true;

    setTasklistProgress(() => {
      const next = { subtasks: [] as boolean[], taskMarkedComplete: true };
      if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
        saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
      }
      return next;
    });
    if (learningCenterTaskId != null) {
      patchLearningCenterTaskCompletion(learningCenterTaskId, true);
    }
    setCelebrationOpen(true);

    void (async () => {
      try {
        if (!canCallTaskCompletionApi) return;
        await putTaskComplete(
          accessToken,
          identityId,
          relayTaskIdForParentCompletion!,
          true,
          subtaskCompleteActor!
        );
        const tasksPayload = await fetchTasksSnapshotAndPersist(accessToken, identityId);
        setTasksSnapshot(tasksPayload);
        lastFetchedTasksPayloadRef.current = tasksPayload;
        await refreshLearningCenterTaskCompletionFromApi();
      } catch (e) {
        console.error('Learning Center task completion update failed:', e);
        setTasklistProgress(() => {
          const next = { subtasks: [] as boolean[], taskMarkedComplete: false };
          if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
            saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
          }
          return next;
        });
        if (learningCenterTaskId != null) {
          patchLearningCenterTaskCompletion(learningCenterTaskId, false);
        }
        setCelebrationOpen(false);
        try {
          await refreshLearningCenterTaskCompletionFromApi();
        } catch {
          /* ignore */
        }
      } finally {
        taskFooterSyncInflightRef.current = false;
      }
    })();
  }, [
    accessToken,
    identityId,
    canCallTaskCompletionApi,
    relayTaskIdForParentCompletion,
    subtaskCompleteActor,
    learningCenterTaskId,
    pageId,
    patchLearningCenterTaskCompletion,
    persistTasklistPageProgressToLocalOnly,
    refreshLearningCenterTaskCompletionFromApi,
    selectedStudent?.accountId,
    visibleSubtasks.length,
    teacherPreview,
  ]);

  const handleMarkUnread = useCallback(() => {
    if (teacherPreview) return;
    if (taskFooterSyncInflightRef.current) return;
    taskFooterSyncInflightRef.current = true;

    setCelebrationOpen(false);
    setCompletionModalOpen(false);

    setTasklistProgress(() => {
      const next = { subtasks: [] as boolean[], taskMarkedComplete: false };
      if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
        saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
      }
      return next;
    });
    if (learningCenterTaskId != null) {
      patchLearningCenterTaskCompletion(learningCenterTaskId, false);
    }

    void (async () => {
      try {
        if (!canCallTaskCompletionApi) return;
        await putTaskComplete(
          accessToken,
          identityId,
          relayTaskIdForParentCompletion!,
          false,
          subtaskCompleteActor!
        );
        const tasksPayload = await fetchTasksSnapshotAndPersist(accessToken, identityId);
        setTasksSnapshot(tasksPayload);
        lastFetchedTasksPayloadRef.current = tasksPayload;
        await refreshLearningCenterTaskCompletionFromApi();
      } catch (e) {
        console.error('Learning Center task completion update failed:', e);
        setTasklistProgress(() => {
          const next = { subtasks: [] as boolean[], taskMarkedComplete: true };
          if (persistTasklistPageProgressToLocalOnly && pageId && selectedStudent?.accountId) {
            saveTasklistPageProgress(selectedStudent.accountId, pageId, next);
          }
          return next;
        });
        if (learningCenterTaskId != null) {
          patchLearningCenterTaskCompletion(learningCenterTaskId, true);
        }
        try {
          await refreshLearningCenterTaskCompletionFromApi();
        } catch {
          /* ignore */
        }
      } finally {
        taskFooterSyncInflightRef.current = false;
      }
    })();
  }, [
    accessToken,
    identityId,
    canCallTaskCompletionApi,
    relayTaskIdForParentCompletion,
    subtaskCompleteActor,
    learningCenterTaskId,
    pageId,
    patchLearningCenterTaskCompletion,
    persistTasklistPageProgressToLocalOnly,
    refreshLearningCenterTaskCompletionFromApi,
    selectedStudent?.accountId,
    teacherPreview,
  ]);

  useLayoutEffect(() => {
    if (!tocItems.length || !isPageVisibleForUser) return;

    const scheduleSync = () => {
      if (tocScrollRafRef.current != null) return;
      tocScrollRafRef.current = window.requestAnimationFrame(() => {
        tocScrollRafRef.current = null;
        syncTocActiveFromScroll();
      });
    };

    const scheduleLockClear = () => {
      if (tocLockClearTimerRef.current != null) {
        clearTimeout(tocLockClearTimerRef.current);
      }
      tocLockClearTimerRef.current = setTimeout(() => {
        tocLockClearTimerRef.current = null;
        if (tocNavLockRef.current) {
          tocNavLockRef.current = null;
          syncTocActiveFromScroll();
        }
      }, 150);
    };

    const onScroll = () => {
      scheduleSync();
      scheduleLockClear();
    };

    const unsubscribe = subscribeLayoutBodyScroll(onScroll);

    return () => {
      unsubscribe();
      if (tocScrollRafRef.current != null) {
        window.cancelAnimationFrame(tocScrollRafRef.current);
        tocScrollRafRef.current = null;
      }
      if (tocLockClearTimerRef.current != null) {
        clearTimeout(tocLockClearTimerRef.current);
        tocLockClearTimerRef.current = null;
      }
    };
  }, [tocItems, tocIdsKey, pageId, syncTocActiveFromScroll, isPageVisibleForUser]);

  useEffect(() => {
    if (!pageId || !/^[a-zA-Z0-9_-]+$/.test(pageId)) {
      setLoadError(true);
      setFetchedPageJson(null);
      return;
    }

    setLoadError(false);

    if (syncPageJson) {
      setFetchedPageJson(null);
      return;
    }

    let cancelled = false;
    setFetchedPageJson(null);
    const ac = new AbortController();

    fetch(`/data/pages/${pageId}.json`, { signal: ac.signal })
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then((json: TasklistPageJson) => {
        if (!cancelled) setFetchedPageJson(finalizePageJson(json));
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [pageId, syncPageJson, finalizePageJson]);

  const hasValidPageId = Boolean(pageId && /^[a-zA-Z0-9_-]+$/.test(pageId));
  const isSchoolOverviewPage = pageId === '400';

  const schoolOverviewWaitingForSchool =
    isSchoolOverviewPage && schoolLoading && !schoolData?.onboarding_content;

  const showEmbeddedContentError =
    loadError ||
    !hasValidPageId ||
    (isSchoolOverviewPage &&
      !schoolLoading &&
      Boolean(data) &&
      displayRuntimeReady &&
      visibleContentSections.length === 0 &&
      visibleSubtasks.length === 0 &&
      !schoolData?.onboarding_content);

  const showPageLoadingSkeleton =
    hasValidPageId &&
    !loadError &&
    !showEmbeddedContentError &&
    (schoolOverviewWaitingForSchool || !data || !displayRuntimeReady);

  const fallbackPageTitle = formatPageText(
    data?.title ??
      learningCenterTaskForPage?.title ??
      tasklistHeroRowForPage?.checklistName ??
      'This page'
  );
  const fallbackPageDescription = formatPageText(
    data?.description ??
      data?.preview ??
      learningCenterTaskForPage?.description ??
      tasklistHeroRowForPage?.description ??
      ''
  );

  const renderMarkReadFooter = () =>
    !teacherPreview &&
    !showEmbeddedContentError &&
    visibleSubtasks.length === 0 &&
    hasValidPageId ? (
      <div className="tasklist-json-task-status-footer" aria-label="Document read status">
        <TaskStatus
          variant="tasklistStickyFooter"
          showHeading={false}
          omitTasklistFooterProgress
          allowMarkIncomplete
          completedCount={tasklistProgress.taskMarkedComplete ? 1 : 0}
          totalCount={1}
          onMarkComplete={handleMarkRead}
          taskFinished={tasklistProgress.taskMarkedComplete}
          onMarkIncomplete={handleMarkUnread}
          markCompleteLabel="Mark Read"
          completedHeading="Module complete!"
          markIncompleteLabel="Mark as incomplete"
          idPrefix={`tasklist-page-${pageId ?? 'page'}-read`}
        />
      </div>
    ) : null;

  const resolvedPageRootClass = [
    `setup-your-accounts tasklist-json-page${isTasklistLayout ? ' tasklist-json-page--tasklist' : ''}`,
    teacherPreview
      ? ' tasklist-json-page--teacher-preview'
      : showEmbeddedContentError
        ? ''
        : visibleSubtasks.length > 0
          ? ' tasklist-json-page--has-task-footer'
          : data
            ? ' tasklist-json-page--has-mark-read-footer'
            : '',
    showEmbeddedContentError ? ' WP-Fallback' : '',
  ]
    .filter(Boolean)
    .join('');

  if (showPageLoadingSkeleton) {
    return (
      <div className={pageRootClass}>
        <TasklistJsonPageHeader title="" description="" skeleton variant={headerVariant} />
        <div className="tasklist-json-body">
          <div className="tasklist-json-layout tasklist-json-layout--skeleton">
            <div className="placeholder placeholder-glow rounded col-12" style={{ minHeight: 200 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!showEmbeddedContentError && (!data || !displayRuntimeReady)) {
    return null;
  }

  if (
    !showEmbeddedContentError &&
    data &&
    !isPageVisibleForUser
  ) {
    return (
      <div className="container py-5">
        <p className="text-muted">This content is not available for your account.</p>
      </div>
    );
  }

  const pageTitle = showEmbeddedContentError ? fallbackPageTitle : formatPageText(data!.title);
  const pageDescription = showEmbeddedContentError
    ? fallbackPageDescription
    : formatPageText(data!.description ?? data!.preview ?? '');

  const completionOverlays =
    !teacherPreview && typeof document !== 'undefined'
      ? createPortal(
          <>
            <CompletionCelebration open={celebrationOpen} onDismiss={handleCelebrationDismiss} />
            <CompletionModal
              open={completionModalOpen}
              onDismiss={handleCompletionModalDismiss}
              onKeepLearning={handleCompletionKeepLearning}
              hasNext={completionModalNext.hasNext}
              nextLabel={
                completionModalNext.nextLabel
                  ? formatPageText(completionModalNext.nextLabel)
                  : null
              }
              isAction={visibleSubtasks.length > 0 ? completionModalNext.isAction : false}
            />
          </>,
          document.body
        )
      : null;

  return (
    <div className={resolvedPageRootClass}>
      {completionOverlays}
      <TasklistJsonPageHeader title={pageTitle} description={pageDescription} variant={headerVariant} />

      <div className="tasklist-json-body">
        {showEmbeddedContentError ? (
          <div className="error-page">
            <WpErrorSection />
          </div>
        ) : (
          <div className="tasklist-json-layout">
            {tocItems.length > 0 && (
              <aside className="tasklist-json-layout__toc">
                <OnThisPageNav items={tocItems} activeId={activeId} onNavigate={handleTocNavigate} />
              </aside>
            )}
            <div className="tasklist-json-layout__main">
              <div className="tasklist-json-content-cards">
                {visibleContentSections.map((section, i) =>
                  renderSection(section, i, sectionAnchorId(section, i), tasklistSectionRenderCtx)
                )}
              </div>

              {visibleSubtasks.length > 0 ? (
                <section
                  id={WHAT_NEXT_ID}
                  className="tasklist-json-anchor tasklist-json-section tasklist-json-section-card"
                >
                  <h2 className="heading-2">What To Do Next</h2>
                  <div className="tasklist-json-subtasks-panel">
                    <SubTaskList
                      layout="tasklistAccordion"
                      revealDidThisOnHover
                      items={subtaskListItems}
                      completed={subtaskCompleted}
                      onToggle={handleToggleSubtask}
                      syncingRowIndex={subtaskSyncInFlightIndex}
                      idPrefix={`tasklist-page-${pageId ?? 'page'}-sub`}
                      readOnly={teacherPreview}
                    />
                  </div>
                </section>
              ) : null}
              {visibleKeepLearningItems.length > 0 ? (
                <section id={KEEP_LEARNING_ID} className="tasklist-json-anchor tasklist-json-keep-learning">
                  <div className="tasklist-json-section-card tasklist-json-keep-learning-card">
                    <h2 className="heading-2">Keep Learning</h2>
                    <div className="longform tasklist-json-longform-stack tasklist-json-longform-stack--keep-learning">
                      {visibleKeepLearningItems.map((kl, kli) => (
                        <div
                          key={`kl-${kli}`}
                          className="tasklist-json-keep-learning__block content-block"
                          data-block-type="text"
                        >
                          <div className="longform">
                            <MarkdownText content={formatPageText(kl.markdown)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        )}

        {!showEmbeddedContentError && !teacherPreview && visibleSubtasks.length > 0 ? (
          <div className="tasklist-json-task-status-footer" aria-label="Task progress">
            <TaskStatus
              variant="tasklistStickyFooter"
              showHeading={false}
              onJumpToNextSection={
                subtaskCompleted.filter(Boolean).length < visibleSubtasks.length
                  ? scrollToNextIncompleteSubtask
                  : undefined
              }
              jumpToNextSectionLabel="What To Do Next"
              completedCount={subtaskCompleted.filter(Boolean).length}
              totalCount={visibleSubtasks.length}
              onMarkComplete={handleConfirmTaskDone}
              taskFinished={tasklistProgress.taskMarkedComplete}
              onMarkIncomplete={handleMarkTaskIncomplete}
              idPrefix={`tasklist-page-${pageId ?? 'page'}`}
            />
          </div>
        ) : null}

        {renderMarkReadFooter()}
      </div>
    </div>
  );
};

export default TasklistJsonPage;
